import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Subscription, timer, firstValueFrom, of } from "rxjs";
import { catchError, switchMap, take, timeout, tap } from "rxjs/operators";
import { AI_ENGINE } from "../common";
import { StoreSelectorsService } from "./store-selectors.service";

/**
 * GemmaHealthService — single source of truth for "is Gemma reachable right now?"
 *
 * Poll flow:
 *   - If AI_ENGINE === 'claude' in global-constants.ts → service is inert. Never polls,
 *     never logs about Gemma. `getEffectiveEngine()` always returns 'claude'.
 *   - If AI_ENGINE === 'gemma' → poll `GET /gemmaStatus` every 60 s (first check fires
 *     immediately). Mars proxies the check to the GPU with a 3 s hard timeout and
 *     respects the GEMMA_*_ENABLED kill switches.
 *
 * Every time a component asks which engine to dispatch via `getEffectiveEngine()`,
 * we emit one console.log line so the developer can trace every AI call's engine
 * decision in the browser devtools console.
 */
@Injectable({ providedIn: "root" })
export class GemmaHealthService implements OnDestroy {
    private readonly HEALTH_POLL_MS = 60_000;     // 60 s between polls
    private readonly HEALTH_TIMEOUT_MS = 3_000;   // 3 s per request (honors user requirement)

    // Optimistic start: if the dev chose gemma, assume online until first check proves otherwise.
    // That way the first AI call after page load doesn't wait for the health check to complete.
    // Server-side fallback (aiServices.js / cli-render.mjs) catches any mid-flight Gemma failure.
    private _gemmaOnline$ = new BehaviorSubject<boolean>(AI_ENGINE === "gemma");

    /** Reactive stream for components that want to react to online/offline transitions */
    readonly gemmaOnline$ = this._gemmaOnline$.asObservable();

    private pollSub: Subscription | null = null;

    constructor(
        private http: HttpClient,
        private storeSelector: StoreSelectorsService,
    ) {
        if (AI_ENGINE !== "gemma") {
            console.log(`[AI Engine] AI_ENGINE='${AI_ENGINE}' (deploy-time choice) — Gemma health checks DISABLED, every call uses Claude`);
            return;
        }
        console.log(`[AI Engine] AI_ENGINE='gemma' (deploy-time choice) — starting Gemma health polling every ${this.HEALTH_POLL_MS / 1000}s with ${this.HEALTH_TIMEOUT_MS / 1000}s timeout`);

        // timer(0, N) emits immediately, then every N ms.
        this.pollSub = timer(0, this.HEALTH_POLL_MS)
            .pipe(switchMap(() => this.runCheck$()))
            .subscribe();
    }

    /** Synchronous getter — last-known state. Cheap, safe to call from dispatch paths. */
    isGemmaOnline(): boolean {
        return this._gemmaOnline$.value;
    }

    /**
     * Returns the engine to use for the next dispatch AND logs the decision.
     * This is the main API components use — always call this instead of reading AI_ENGINE directly,
     * because it factors in runtime health on top of the deploy-time choice.
     */
    getEffectiveEngine(): "claude" | "gemma" {
        if (AI_ENGINE === "claude") {
            console.log(`[AI] AI_ENGINE=claude → using Claude (hardcoded, no Gemma check)`);
            return "claude";
        }
        // AI_ENGINE === 'gemma'
        if (this._gemmaOnline$.value) {
            console.log(`[AI] AI_ENGINE=gemma, Gemma ONLINE → using Gemma`);
            return "gemma";
        }
        console.log(`[AI] AI_ENGINE=gemma, Gemma OFFLINE → falling back to Claude (server handles silently)`);
        return "claude";
    }

    /**
     * Force a fresh check now. Resolves with new state. Useful for "is Gemma back?" on demand
     * without waiting for the next 60 s poll tick.
     */
    async forceCheck(): Promise<boolean> {
        if (AI_ENGINE !== "gemma") return false;
        await firstValueFrom(this.runCheck$());
        return this._gemmaOnline$.value;
    }

    private runCheck$() {
        const t0 = Date.now();
        console.log(`[AI Health] Checking Gemma...`);

        return this.storeSelector.getAppSettings().pipe(
            take(1),
            switchMap((settings) => {
                const base = (settings && settings.globalService) || "";
                const url = `${base}/gemmaStatus`;
                return this.http.get<GemmaStatusResponse>(url).pipe(
                    timeout({ each: this.HEALTH_TIMEOUT_MS }),
                    tap((body) => this.applyResult(body, Date.now() - t0)),
                    catchError((err) => {
                        const elapsed = Date.now() - t0;
                        this.applyResult({
                            online: false,
                            reason: err?.name === "TimeoutError" ? "client-side 3s timeout" : (err?.message || "HTTP error"),
                            latencyMs: elapsed,
                        }, elapsed);
                        return of(null as unknown as GemmaStatusResponse);
                    }),
                );
            }),
        );
    }

    private applyResult(body: GemmaStatusResponse, elapsedMs: number) {
        const wasOnline = this._gemmaOnline$.value;
        const nowOnline = !!(body && body.online);

        if (nowOnline) {
            const modelWarn = body.hasVideoModel === false || body.hasSceneModel === false
                ? `  ⚠️  missing model: ${JSON.stringify({ video: body.hasVideoModel, scene: body.hasSceneModel })}`
                : "";
            console.log(`[AI Health] Gemma ONLINE (${elapsedMs}ms)${modelWarn}`);
        } else {
            const reasonLower = (body?.reason || "").toLowerCase();
            const timedOut = reasonLower.includes("timeout") || elapsedMs >= this.HEALTH_TIMEOUT_MS - 200;
            if (timedOut) {
                console.log(`[AI Health] Gemma not reachable within ${this.HEALTH_TIMEOUT_MS / 1000} seconds, falling back to Claude (reason: ${body?.reason || "timeout"})`);
            } else {
                console.log(`[AI Health] Gemma OFFLINE — ${body?.reason || "unknown"} (${elapsedMs}ms)`);
            }
        }

        if (wasOnline !== nowOnline) {
            console.log(`[AI Health] state TRANSITION: ${wasOnline ? "online" : "offline"} → ${nowOnline ? "online" : "offline"}`);
        }
        this._gemmaOnline$.next(nowOnline);
    }

    ngOnDestroy() {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = null;
        }
    }
}

/** Shape returned by mars /gemmaStatus */
interface GemmaStatusResponse {
    online: boolean;
    reason?: string;
    latencyMs?: number;
    checkedAt?: string;
    hasVideoModel?: boolean;
    hasSceneModel?: boolean;
    partialKillSwitch?: { scene: boolean; video: boolean } | null;
}
